import {NgTemplateOutlet} from "@angular/common"
import {Component, computed, DestroyRef, effect, ElementRef, inject, input, OnInit, afterNextRender, Injector, ChangeDetectionStrategy} from "@angular/core"
import {ColorInputSocketComponent} from "@material-editor/components/sockets/color-input-socket/color-input-socket.component"
import {NodeSocketComponent} from "@app/socket-editor/components/node-socket/node-socket.component"
import {PlainInputSocketComponent} from "@material-editor/components/sockets/plain-input-socket/plain-input-socket.component"
import {ScalarInputSocketComponent} from "@material-editor/components/sockets/scalar-input-socket/scalar-input-socket.component"
import {VectorInputSocketComponent} from "@material-editor/components/sockets/vector-input-socket/vector-input-socket.component"
import {DragService} from "@node-editor/services/drag.service"
import {NodeIoComponent} from "@app/socket-editor/components/node-io/node-io.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {Color} from "@cm/graph-v3"
import {PreviewSceneService} from "@material-editor/services/preview-scene.service"
import {MaterialNode, MaterialNodeData, Vec3} from "@cm/material-nodes"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-node-io",
    templateUrl: "./material-node-io.component.html",
    styleUrls: ["./material-node-io.component.scss"],
    imports: [
        ColorInputSocketComponent,
        VectorInputSocketComponent,
        NodeSocketComponent,
        ScalarInputSocketComponent,
        PlainInputSocketComponent,
        NgTemplateOutlet,
    ],
    host: {"[hidden]": "!$active()"},
})
export class MaterialNodeIoComponent extends NodeIoComponent<MaterialNodeData> implements OnInit {
    readonly $valueType = computed(() => this.$socket().valueType)
    readonly $range = computed(() => this.$socket().range)
    readonly $value = computed(() => this.$socket().value)
    readonly $disableEditingIfConnected = input(true, {alias: "disableEditingIfConnected"})
    readonly $material = input.required<MaterialNode>({alias: "material"})

    readonly $active = input<boolean>(true, {alias: "active"})

    readonly element = inject(ElementRef)
    readonly destroyRef = inject(DestroyRef)
    private readonly injector = inject(Injector)
    readonly drag = inject(DragService)
    readonly previewSceneService = inject(PreviewSceneService)

    constructor() {
        super()

        effect(() => {
            const value = this.$active()
            if (!value) {
                this.connections.deactivate(this.$nodeId(), {direction: this.$socket().type, id: this.$socket().id})
            }
        })
    }

    ngOnInit() {
        // a socket changes height when it gains or loses a connection, which moves every socket below it
        const setConnected = (connected: boolean) => {
            if (this.$connected() === connected) return
            this.$connected.set(connected)
            afterNextRender(() => void this.connections.refreshSocketPositions(this.$nodeId()), {injector: this.injector})
        }

        setConnected(!!this.connections.inputs(this.$nodeId(), this.$socket().id)?.length)
        this.connections
            .changesForSocket(this.$nodeId(), this.$socket().id, "input")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((socketConnections) => setConnected(socketConnections.length > 0))
    }

    changeValue({value, type: _type}: {value: unknown; type: string}) {
        this.$material().updateSetting(this.$socket().id, value)
        this.previewSceneService.$refresh.next()
    }

    readonly $valueAsColor = computed(() => this.$value() as Color | undefined)
    readonly $valueAsVec3 = computed(() => this.$value() as Vec3 | undefined)
    readonly $valueAsNumber = computed(() => this.$value() as number | undefined)
    readonly $valueAsString = computed(() => this.$value() as string | undefined)
}
